import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { nexusApi } from '../api/nexus';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';

const AnalystStudio = () => {
  const navigate = useNavigate();
  const { user, isManagement, isAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [insight, setInsight] = useState(null);
  const [trendsLibrary, setTrendsLibrary] = useState(null);

  useEffect(() => {
    if (user?.level >= 8) {
      loadTrendsLibrary();
    }
  }, [user]);

  const loadTrendsLibrary = async () => {
    try {
      const response = await nexusApi.analystTrendsLibrary();
      setTrendsLibrary(response.data);
    } catch (error) {
      console.error('Failed to load trends library:', error);
    }
  };

  if (user?.level < 8) {
    return (
      <div className="nexus-content overflow-hidden bg-white">
        <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
                <span className="text-white font-black text-2xl">📊</span>
              </div>
              <div>
                <h1 className="text-xl font-black tracking-wide text-white">Analyst Studio</h1>
                <p className="text-sm text-white font-medium">Analyze trends and generate insights</p>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105" data-testid="back-button">
              ← Back
            </Button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-0">
          <Card className="shadow-xl border-0" data-testid="level-locked-card" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}}>
            <CardContent className="text-center py-12">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className="text-xl font-semibold mb-2">Level 8 Required</h3>
              <p className="text-gray-600 mb-4">Analyst Studio requires Level 8 or higher</p>
              <p className="text-sm text-gray-500 mb-6">Your current level: {user?.level || 1}</p>
              <Button onClick={() => navigate('/dashboard')} className="bg-emzi-blue hover:bg-blue-600 text-white" data-testid="back-dashboard-button">Back to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleAnalyzeTrend = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.analystAnalyze({
        link,
        user_level: user.level
      });
      setAnalysis(response.data);
      setStep(2);
    } catch (error) {
      console.error('Failed to analyze trend:', error);
      alert('Analysis failed. Make sure the link is accessible.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsight = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.analystInsightGenerate({
        trend_id: analysis.trend_id
      });
      setInsight(response.data);
      setStep(3);
    } catch (error) {
      console.error('Failed to generate insight:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestApproval = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.analystInsightApprove({
        insight_id: insight.insight_id
      });
      alert(`Approval requested! Status: ${response.data.approval_status}`);
      setStep(4);
    } catch (error) {
      console.error('Failed to request approval:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <Alert className="bg-green-50 border-green-200">
        <AlertDescription>
          ✓ Level {user.level} Access - Analyst Studio Unlocked
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <label className="text-sm font-medium">Trend Link</label>
        <Input
          type="url"
          placeholder="https://tiktok.com/trend/..."
          value={link}
          onChange={(e) => setLink(e.target.value)}
          data-testid="trend-link-input"
        />
        <p className="text-xs text-gray-500">Paste a link to TikTok trend, viral post, or market report</p>
      </div>

      <Button
        onClick={handleAnalyzeTrend}
        disabled={loading || !link.trim()}
        className="w-full bg-emzi-blue hover:bg-blue-600 text-white font-semibold h-12"
        data-testid="analyze-trend-button"
      >
        {loading ? 'Analyzing...' : 'Analyze Trend →'}
      </Button>

      {trendsLibrary && trendsLibrary.trends?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Saved Trends ({trendsLibrary.count})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trendsLibrary.trends.map((trend) => (
                <div key={trend.trend_id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <div>
                    <p className="text-sm font-medium">{trend.title}</p>
                    <p className="text-xs text-gray-500">{trend.created_at}</p>
                  </div>
                  <Badge variant={trend.status === 'approved' ? 'default' : 'secondary'}>
                    {trend.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {analysis && (
        <>
          <Alert className="bg-blue-50 border-blue-200" data-testid="analysis-complete-alert">
            <AlertDescription>
              ✓ Trend Analysis Complete: {analysis.trend_id}
            </AlertDescription>
          </Alert>

          <Card data-testid="trend-analysis-card">
            <CardHeader>
              <CardTitle className="text-base">{analysis.detected_trend}</CardTitle>
              <CardDescription>Source: {analysis.platform_source}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Relevance Score</p>
                  <p className="text-2xl font-bold text-green-600">{analysis.relevance_score}/100</p>
                </div>
                <div>
                  <p className="text-gray-600">Category</p>
                  <Badge className="mt-1">{analysis.category}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleGenerateInsight}
            disabled={loading}
            className="w-full"
            data-testid="generate-insight-button"
          >
            {loading ? 'Generating...' : 'Generate Insight →'}
          </Button>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {insight && (
        <>
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription>
              ✓ Insight Generated: {insight.insight_id}
            </AlertDescription>
          </Alert>

          <Card data-testid="insight-card">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-base">{insight.title}</CardTitle>
                  <CardDescription>{insight.status}</CardDescription>
                </div>
                <Badge variant="default">Score: {insight.ai_pre_score}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Description:</p>
                <p className="text-sm">{insight.description}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Applicability:</p>
                <p className="text-sm">{insight.applicability}</p>
              </div>
              {insight.requires_approval && (
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertDescription>
                    ⚠️ Requires Supervisor Approval
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setStep(1); setInsight(null); setAnalysis(null); }}
              className="flex-1"
              data-testid="new-trend-button"
            >
              New Trend
            </Button>
            <Button
              onClick={handleRequestApproval}
              disabled={loading}
              className="flex-1"
              data-testid="request-approval-button"
            >
              {loading ? 'Requesting...' : 'Request Approval'}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription>
          📨 Approval request sent to Supervisor queue
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600">1.</span>
              <span>Supervisor will review your insight</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600">2.</span>
              <span>If approved, insight will be added to Trend Library</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600">3.</span>
              <span>You'll earn BP bonus for approved insights</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => { setStep(1); setInsight(null); setAnalysis(null); }}
          className="flex-1"
          data-testid="analyze-another-button"
        >
          Analyze Another
        </Button>
        <Button
          onClick={() => navigate('/dashboard')}
          className="flex-1"
          data-testid="back-dashboard-button"
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );

  return (
    <div className="nexus-content overflow-hidden bg-white">
      {/* EMZI Header - Normalized with Dashboard */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <span className="text-white font-black text-2xl">📊</span>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white">Analyst Studio</h1>
              <p className="text-sm text-white font-medium">Analyze trends and generate insights (Level 8+)</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {(isManagement() || isAdmin()) && (
              <span className="text-white text-xs font-medium opacity-90" title="System certified under EMZI ISO-Aligned Compliance Protocol">
                🟢 ISO Compliance Active
              </span>
            )}
            <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105" data-testid="back-button">
              ← Back
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-0">
        <Card className="shadow-xl border-0" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}} data-testid="analyst-studio-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emzi-blue bg-opacity-10 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-emzi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-gray-900 font-bold">Analyst Studio</CardTitle>
                <CardDescription className="text-sm text-gray-600">Analyze trends and generate insights (Level 8+)</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-emzi-blue' : 'bg-gray-200'
                  }`}
                  data-testid={`progress-step-${s}`}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalystStudio;
